// owned by WP-SEC-UI · the signed-in session as the shell shows it: last sign-in, idle countdown, absolute limit, other sessions
// (`GET /api/auth/session` → SessionInfo, SPEC C12; shell-ia §11.1). The status bar, the account menu and the arrival message read it.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi, useAuth, useSessionClock } from "@ncp/api-client";
import type { SessionInfo } from "@ncp/assistant-sdk/contracts";

/**
 * `GET /api/auth/session`, refreshed every 5 minutes and after each token refresh. Null for sessions from before the strict sign-in
 * and while the server does not serve it yet; callers then show what the stored session knows (`previousSignIn`), never a guess.
 */
export function useSessionInfo(): { info: SessionInfo | null; loading: boolean } {
  const api = useApi();
  const { session, managed } = useAuth();
  const q = useQuery({
    queryKey: ["auth", "session", session?.sessionId ?? null],
    queryFn: async () => {
      try {
        return await api.get<SessionInfo>("/api/auth/session");
      } catch {
        return null;
      }
    },
    enabled: managed && !!session,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  return { info: q.data ?? null, loading: q.isLoading };
}

/** Milliseconds left before the idle sign-out and before the 10-hour limit, ticking every second (null when not applicable). */
export function useSessionCountdown(): { idleLeftMs: number | null; absoluteLeftMs: number | null } {
  const clock = useSessionClock();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return {
    idleLeftMs: clock.idleDeadline === null ? null : Math.max(0, clock.idleDeadline - now),
    absoluteLeftMs: clock.absoluteDeadline === null ? null : Math.max(0, clock.absoluteDeadline - now),
  };
}

/** "26:41" — minutes and seconds, Western digits in both languages. */
export function countdownText(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** "Chrome on macOS" from a user agent string, when the server sends a raw one; otherwise the server's own device text. */
export function deviceLabel(device: string | null | undefined): string | null {
  if (!device) return null;
  if (!/Mozilla\/|AppleWebKit|Gecko\//.test(device)) return device;
  const browser = /Edg\//.test(device) ? "Edge" : /Chrome\//.test(device) ? "Chrome" : /Firefox\//.test(device) ? "Firefox" : /Safari\//.test(device) ? "Safari" : null;
  const os = /Mac OS X|Macintosh/.test(device) ? "macOS" : /Windows/.test(device) ? "Windows" : /iPhone|iPad/.test(device) ? "iOS" : /Android/.test(device) ? "Android" : /Linux/.test(device) ? "Linux" : null;
  return browser && os ? `${browser} · ${os}` : (browser ?? os ?? null);
}
